import { type ForwardRefRenderFunction, forwardRef, useState } from 'react'

import {
  LocalizationProvider,
  DatePicker as MUIDatePicker,
  type DatePickerProps as MUIDatePickerProps,
  PickersDay,
} from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DSInputType, Icon, Tooltip } from 'components'

import { TextFieldStyled } from '../Input/styles'
import * as S from './styles'
import 'dayjs/locale/pt-br'

import { useAppTheme } from 'styles/theme'
import { theme } from 'styles/theme/hav-theme'

export type DatePickerProps<T> = {
  error?: string | null
  errorLabel?: string | null
  errorState?: boolean
  required?: boolean
  height?: number
  placeholder?: string
  readOnlyInput?: boolean
} & Omit<MUIDatePickerProps<T, T>, 'renderInput'>

const DatePickerComponent: ForwardRefRenderFunction<HTMLInputElement, DatePickerProps<string>> = (
  {
    error,
    errorLabel,
    errorState,
    required,
    height = 40,
    placeholder,
    readOnlyInput = false,
    ...props
  },
  ref,
) => {
  const { colors } = useAppTheme()
  const [isOpen, setIsOpen] = useState(false)
  const hasError = Boolean(error)

  return (
    <LocalizationProvider adapterLocale="pt-br" dateAdapter={AdapterDayjs}>
      <div>
        <MUIDatePicker
          {...props}
          components={{
            OpenPickerIcon: () => (
              <Icon color={theme.colors.ion.primary.main} icon="calendar-days" size="16" />
            ),
          }}
          desktopModeMediaQuery="(min-width: 600px)"
          InputProps={{
            size: 'small',
          }}
          OpenPickerButtonProps={{ color: 'primary' }}
          onClose={() => setIsOpen(false)}
          onOpen={() => setIsOpen(true)}
          open={isOpen}
          PaperProps={{
            style: {
              textTransform: 'capitalize',
            },
          }}
          renderDay={(_, __, pickerDayProps) => (
            <S.PickersDayWrapper key={pickerDayProps.key}>
              <PickersDay {...pickerDayProps} />
            </S.PickersDayWrapper>
          )}
          renderInput={({ name, label, ...params }) => (
            <S.TextFieldWrapper>
              {label && (
                <S.LabelWrapper disabled={params.disabled}>
                  <label htmlFor={name} id="LabelInputForm">
                    {label}
                  </label>
                  {errorLabel && (
                    <Tooltip arrow placement="top" title={errorLabel}>
                      <Icon color={colors.alert.danger[30]} icon="error" size="12" />
                    </Tooltip>
                  )}
                </S.LabelWrapper>
              )}
              <TextFieldStyled
                design={DSInputType.MASTER_INPUT}
                height={height}
                id={name}
                inputRef={ref}
                name={name}
                required={required}
                {...params}
                error={hasError || errorState}
                helperText={error}
                inputProps={{
                  ...params.inputProps,
                  placeholder: placeholder ?? params.inputProps?.placeholder,
                  readOnly: readOnlyInput,
                  style: {
                    cursor: readOnlyInput ? 'pointer' : 'text',
                  },
                }}
                onClick={() => !params.disabled && setIsOpen(true)}
              />
            </S.TextFieldWrapper>
          )}
          showDaysOutsideCurrentMonth
        />
      </div>
    </LocalizationProvider>
  )
}

export const DatePicker = forwardRef(DatePickerComponent)
